import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The Messaging page's conversation table ("Threads" in the design) — extracted from
 * `messaging-screen.tsx` by plan-004 WC-81 and HANDED TO WC-90 (plan §4.3), which gives it the
 * columns the design names and links a visitor's earlier conversations (design §17.10.7, FR-WC-21).
 *
 * WHAT IS IN IT. Every conversation on one of this workspace's website widgets, typed ("Website
 * chat") or spoken ("Website voice"). The console's own test chat is not a visitor and is not here
 * (History lists it, marked "Test chat"); phone calls and browser tests are in the agent's History.
 *
 * THE COLUMNS, AS DRAWN: Contact, Channel, Last message, Outcome, When. The contact is the visitor —
 * "Visitor 8F22A1", from the API's `visitor_tag`, never the key the widget keeps in
 * `party_address`. A visitor who wrote before carries "2 conversations", which opens the agent's
 * History on exactly that visitor's conversations (`party`). The last message is withheld from a
 * Viewer, whose role reads outcomes rather than what was said, and the cell says so.
 *
 * WHAT THE DESIGN HAD AND THIS DOES NOT: the Today / 7d / 30d / All segmented control.
 * `GET /v1/messaging/conversations` returns the fifty most recent and takes no range parameter, so a
 * control here would either filter fifty rows and call it "all", or look like it queried and not
 * have. The table says what it is instead.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import type { ReactNode } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { channelLabelWordOf, partyLabelOf } from "../../api/conversation-view.js";
import type { WebChatConversationRowView } from "../../api/gateway.js";
import { webChatConversationQuery } from "../../api/query.js";

/** The Last message cell: the line, or why there is none. */
export function lastMessageTextOf(row: Pick<WebChatConversationRowView, "lastMessage" | "transcriptWithheld">): string {
  if (row.transcriptWithheld) return portalText("operations", "your_role_reads_outcomes_not_messages_727f7742");
  return row.lastMessage ?? "—";
}

/** The Outcome cell: the template's own word, or how it ended. */
export function outcomeTextOf(row: Pick<WebChatConversationRowView, "outcome" | "endReason">): string {
  if (row.outcome !== null) return row.outcome;
  switch (row.endReason) {
    case "visitor_idle":
      return portalText("operations", "visitor_went_quiet_10095a03");
    case "caller_hangup":
      return portalText("operations", "visitor_left_46e1084a");
    // plan-022 (design §34): the agent ended the finished chat itself.
    case "voice_agent_ended":
      return portalText("operations", "the_agent_ended_the_conversation_a9ef70e9");
    case "max_duration_reached":
      return portalText("operations", "reached_the_time_limit_0d457e46");
    case "turn_ceiling_reached":
      return portalText("operations", "reached_the_message_limit_e2ab9a8c");
    // Review of plan-004, open item 1 (2026-09-26): the runtime serving it stopped; the worker's
    // sweep ended it at the last reply.
    case "runtime_lost":
      return portalText("operations", "ended_unexpectedly_2cc391da");
    // 2026-10-03 (fault containment): a runtime fault ended this conversation, and only it.
    case "runtime_fault":
      return portalText("operations", "ended_by_a_technical_problem_dbefff7a");
    case "in_progress":
      return portalText("operations", "in_progress_b6bd42e4");
    default:
      return portalText("operations", "no_outcome_a4fe5d40");
  }
}

export function ConversationTable(): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const webChat = useQuery(webChatConversationQuery(gateway));

  return (
    <>
      <p className="mg-threads-title">{portalText("operations", "threads_bb12e8aa")}</p>
      {webChat.isError ? (
        <p className="mg-empty" role="alert">
          {portalText("operations", "the_conversations_could_not_be_read_nothing_has_changed_re_bda736c9")}</p>
      ) : webChat.data === undefined ? (
        <p className="mg-empty">{portalText("operations", "reading_your_conversations_21f8fbfa")}</p>
      ) : webChat.data.length === 0 ? (
        <p className="mg-empty">
          {portalText("operations", "no_website_conversations_yet_once_the_chat_snippet_is_on_y_825df667")}</p>
      ) : (
        <>
          <div className="mg-scroll">
            <Table caption={portalText("operations", "recent_website_conversations_981ee65a")} captionVisible={false}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{portalText("operations", "contact_b37456c4")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("operations", "channel_879f0b1b")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("operations", "last_message_83741110")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("operations", "outcome_d3f06106")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("operations", "when_769bb19e")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {webChat.data.map((one) => (
                  <TableRow key={one.conversationId}>
                    <TableCell>
                      <span className="mg-contact">{partyLabelOf(one)}</span>
                      <span className="mg-row-meta">
                        <Link
                          to="/agents/$voiceAgentId"
                          params={{ voiceAgentId: one.voiceAgentId }}
                          search={{
                            tab: "history",
                            range: "all",
                            outcome: "all",
                            q: "",
                            conversation: one.conversationId,
                            ...(one.partyConversationCount > 1 ? { party: one.conversationId } : {}),
                            analyticsRange: "14d",
                            agentVersion: "",
                            concern: "gaps",
                            orderStatus: "all",
                          }}
                        >
                          {one.partyConversationCount > 1
                            ? portalText("operations", "value_conversations_08c41818", { v0: String(String(one.partyConversationCount)) })
                            : portalText("operations", "open_in_history_c4d605f2")}
                        </Link>
                        {" · "}
                        {one.voiceAgentName}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="mg-channel">
                        <Icon glyph={one.medium === "voice" ? GLYPH.browser : GLYPH.chatText} size="md" />
                        {channelLabelWordOf(one.channelLabel)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={one.transcriptWithheld ? "mg-last mg-muted" : "mg-last"}>
                        {lastMessageTextOf(one)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {outcomeTextOf(one)}
                      {one.isTest ? (
                        <>
                          {" "}
                          <Tag tone="outline">{portalText("operations", "test_a94a8fe5")}</Tag>
                        </>
                      ) : null}
                    </TableCell>
                    <TableCell numeric>{one.whenText}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mg-row-meta mg-foot">
            {portalText("operations", "the_fifty_most_recent_newest_first_every_conversation_with_0a9c249e")}</p>
        </>
      )}
    </>
  );
}
